"use client"
import { cn } from "@/lib/utils";
import { useApiGet } from "@/react-queries/custom-request";
import { getAvaliableBalance, getLockedBalance } from "@/react-queries/queries/customers";
import { AvaliableBalanceTypes, LockedBalanceTypes } from "@/react-queries/queries/customers/types";
import { ChevronRight, Loader2Icon, Repeat, Wallet } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import BalanceItem from "./BalanceItem";

const active = "bg-indigo-600 bg-opacity-100 pointer-events-none select-none text-white";

const BalanceMobile = () => {
    const translate = useTranslations("Header.balance");
    const [isOpened, setIsOpened] = useState(false);
    const [balanceType, setBalanceType] = useState<"locked" | "avaliable">("locked");
    const [balance, setBalance] = useState<LockedBalanceTypes[] | AvaliableBalanceTypes[]>();
    const [isMounted, setIsMounted] = useState(false);

    const {
        data: lockedBalance,
        isLoading: lockedLoading,
        isError: lockedError,
        refetch: retryLocked,
    } = useApiGet<LockedBalanceTypes[]>(["locked_balance"], getLockedBalance);

    const {
        data: avaliableBalance,
        isLoading: avaliableLoading,
        isError: avaliableError,
        refetch: retryAvaliable,
    } = useApiGet<AvaliableBalanceTypes[]>(
        ["avaliable_balance"],
        getAvaliableBalance
    );

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (lockedBalance && avaliableBalance) {
            if (balanceType === "locked") {
                // @ts-ignore
                setBalance(lockedBalance);
            } else {
                // @ts-ignore
                setBalance(avaliableBalance);
            }
        }
    }, [balanceType, lockedBalance, avaliableBalance]);

    if (!isMounted) {
        return (
            <div className={'transition-transform overflow-hidden rounded-sm w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-none'}>
                <div className="flex flex-col gap-4 dark:text-white font-bold p-4">
                    <p className="flex items-center justify-between">
                        <p className="flex items-center gap-2">
                            <Wallet/>
                            {translate("title")}
                        </p>
                        <ChevronRight className={cn('transition-transform', 'rotate-0')} />
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={'transition-transform overflow-hidden rounded-sm w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-none'} onClick={() => setIsOpened(!isOpened)}>
            <div className="flex flex-col gap-4 dark:text-white font-bold p-4">
                <p
                    className="flex items-center justify-between"
                >
                    <p className="flex items-center gap-2">
                        <Wallet/>
                        {translate("title")}
                    </p>
                    <ChevronRight className={cn('transition-transform', isOpened ? 'rotate-90' : 'rotate-0')} />
                </p>
                {
                    isOpened &&
                    <div className="flex gap-2 flex-col" onClick={(e) => e.stopPropagation()}>
                        <span
                            onClick={() => setBalanceType("locked")}
                            className={cn(
                                "w-full text-center text-sm font-normal rounded-sm p-2 bg-slate-200 bg-opacity-25 hover:opacity-80",
                                balanceType === "locked" && active
                            )}
                            role="button"
                        >
                            {translate("locked")}
                        </span>
                        <span
                            onClick={() => setBalanceType("avaliable")}
                            className={cn(
                                "w-full text-center text-sm font-normal rounded-sm p-2 bg-slate-200 bg-opacity-25 hover:opacity-80",
                                balanceType === "avaliable" && active
                            )}
                            role="button"
                        >
                            {translate("available")}
                        </span>
                    </div>
                }

            </div>
            {
                isOpened &&
                <div className="dark:even:[&>div]:bg-slate-600 even:[&>div]:bg-slate-50 max-h-[150px] overflow-y-auto scrollbar" onClick={(e) => e.stopPropagation()}>
                    {balance && balance.length > 0 ? (
                        balance.map((b, i) => (
                            <BalanceItem
                                key={`${balanceType}-mobile-${b.currency_code}-${b.id}-${i}`}
                                amount={b.balance}
                                amountUSD={b.usd_amount}
                                fullName={b.name}
                                shortName={b.currency_code}
                                img={b.currency_code.toLowerCase()}
                                type={b.type as "crypto" | "currency"}
                            />
                        ))
                    ) : lockedError || avaliableError ? (
                        <div className="bg-red-400 bg-opacity-25 text-red-700 dark:text-white flex items-center p-4 text-xs gap-2">
                            An error occured while getting {balanceType} balance
                            <button
                                className="bg-red-700 text-white rounded-sm shrink-0 p-2 flex items-center gap-2"
                                onClick={() => {
                                    if (balanceType === "locked") return retryLocked();
                                    return retryAvaliable();
                                }}
                            >
                                {avaliableLoading || lockedLoading ? (
                                    <Loader2Icon
                                        width={15}
                                        height={15}
                                        className="shrink-0 animate-spin"
                                    />
                                ) : (
                                    <>
                                        Try again
                                        <Repeat width={15} height={15} className="shrink-0" />
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="w-full p-4 text-center">No balance</div>
                    )}
                </div>                
            }

        </div>
    );
};

export default BalanceMobile;